import React, { useEffect, useState } from 'react';
import { employeesAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Users, BookOpen, GraduationCap, Search, ChevronLeft, ChevronRight, AlertCircle, Save, X, Edit, Loader2, ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw } from 'lucide-react';

export default function TeacherAssignments() {
  const [teachers, setTeachers] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Professores ocultos da lista
  const [hiddenTeachers, setHiddenTeachers] = useState(() => {
    const saved = localStorage.getItem('hidden_teachers');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [showHidden, setShowHidden] = useState(false); // Mostrar professores ocultos
  
  // Modal de vinculação
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [onlyActive, setOnlyActive] = useState(true);
  
  // Dados para vinculação
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [availableClasses, setAvailableClasses] = useState({});
  const [classSubjects, setClassSubjects] = useState({}); // Disciplinas por turma
  const [selectedAssignments, setSelectedAssignments] = useState({}); // {classId: {selected: true, subjects: [ids]}}
  const [saving, setSaving] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState({});
  
  // Expandir/colapsar
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [expandedClasses, setExpandedClasses] = useState(new Set()); // Para mostrar disciplinas
  
  // Filtro por disciplina
  const [allSubjects, setAllSubjects] = useState([]); // Todas as disciplinas disponíveis
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState(null); // Disciplina selecionada para filtro
  const [selectedSubjectsMultiple, setSelectedSubjectsMultiple] = useState(new Set()); // Múltiplas disciplinas selecionadas
  const [loadingAllSubjects, setLoadingAllSubjects] = useState(false);
  const [applyingSubjects, setApplyingSubjects] = useState(false); // Loading ao aplicar
  const [selectedEducationLevel, setSelectedEducationLevel] = useState('all'); // Filtro por nível de ensino

  // Níveis de ensino disponíveis (valores devem corresponder exatamente ao campo education_level das turmas)
  const educationLevels = [
    { value: 'all', label: 'Todos os Níveis' },
    { value: 'Educação Infantil', label: 'Ed. Infantil' },
    { value: 'Ensino Fundamental I', label: 'Fundamental I' },
    { value: 'Ensino Fundamental II', label: 'Fundamental II' },
    { value: 'Ensino Médio', label: 'Ensino Médio' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesRes, classGroupsRes] = await Promise.all([
        employeesAPI.list({ is_teacher: true }),
        api.get('/class-groups')
      ]);
      
      setTeachers(employeesRes.data.filter(e => e.is_teacher));
      setClassGroups(classGroupsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Carregar todas as disciplinas únicas
  const loadAllSubjects = async () => {
    if (allSubjects.length > 0) return;
    setLoadingAllSubjects(true);
    try {
      const res = await api.get('/curriculum-subjects');
      // Remover duplicatas por nome
      const uniqueSubjects = [];
      const seen = new Set();
      res.data.forEach(s => {
        if (!seen.has(s.name)) {
          seen.add(s.name);
          uniqueSubjects.push(s);
        }
      });
      setAllSubjects(uniqueSubjects.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Erro ao carregar disciplinas:', error);
    } finally {
      setLoadingAllSubjects(false);
    }
  };

  const loadSubjectsForClass = async (classId) => {
    if (classSubjects[classId]) return; // Já carregou
    
    setLoadingSubjects(prev => ({ ...prev, [classId]: true }));
    try {
      const res = await api.get(`/class-groups/${classId}/subjects`);
      setClassSubjects(prev => ({ ...prev, [classId]: res.data }));
    } catch (error) {
      console.error('Erro ao carregar disciplinas:', error);
      setClassSubjects(prev => ({ ...prev, [classId]: [] }));
    } finally {
      setLoadingSubjects(prev => ({ ...prev, [classId]: false }));
    }
  };

  const openTeacherDialog = async (teacher) => {
    setSelectedTeacher(teacher);
    setDialogOpen(true);
    setSelectedAssignments({});
    setExpandedClasses(new Set());
    setClassSubjects({});
    
    // Carregar vinculações existentes do professor
    try {
      const res = await api.get(`/teacher-assignments?teacher_id=${teacher.id}`);
      setTeacherAssignments(res.data);
      
      // Converter vinculações para o formato de seleção
      const assignments = {};
      res.data.forEach(a => {
        assignments[a.class_group_id] = {
          selected: true,
          subjects: a.subject_ids || []
        };
      });
      setSelectedAssignments(assignments);
      
      // Organizar turmas por curso
      organizeClassesByCourse();
    } catch (error) {
      console.error('Erro ao carregar vinculações:', error);
      setTeacherAssignments([]);
      setSelectedAssignments({});
    }
  };

  const organizeClassesByCourse = () => {
    const grouped = {};
    classGroups.forEach(cg => {
      const nivel = cg.education_level || 'Outros';
      if (!grouped[nivel]) {
        grouped[nivel] = [];
      }
      grouped[nivel].push(cg);
    });
    
    setAvailableClasses(grouped);
    setExpandedCourses(new Set(Object.keys(grouped)));
  };

  const toggleCourse = (course) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(course)) {
      newExpanded.delete(course);
    } else {
      newExpanded.add(course);
    }
    setExpandedCourses(newExpanded);
  };

  const toggleClassExpansion = async (classId) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(classId)) {
      newExpanded.delete(classId);
    } else {
      newExpanded.add(classId);
      // Carregar disciplinas se ainda não carregou
      await loadSubjectsForClass(classId);
    }
    setExpandedClasses(newExpanded);
  };

  const toggleClass = (classId) => {
    setSelectedAssignments(prev => {
      const current = prev[classId] || { selected: false, subjects: [] };
      if (current.selected) {
        // Desmarcar turma
        const { [classId]: _, ...rest } = prev;
        return rest;
      } else {
        // Marcar turma com todas as disciplinas
        return {
          ...prev,
          [classId]: { selected: true, subjects: [] } // [] = todas
        };
      }
    });
  };

  const toggleSubject = (classId, subjectId) => {
    setSelectedAssignments(prev => {
      const current = prev[classId] || { selected: true, subjects: [] };
      const subjects = current.subjects || [];
      const allSubjects = classSubjects[classId] || [];
      
      let newSubjects;
      if (subjects.length === 0) {
        // Estava com todas, agora vai ter só algumas
        // Se clicar em uma, seleciona todas menos ela
        newSubjects = allSubjects.filter(s => s.id !== subjectId).map(s => s.id);
      } else if (subjects.includes(subjectId)) {
        // Remover
        newSubjects = subjects.filter(id => id !== subjectId);
      } else {
        // Adicionar
        newSubjects = [...subjects, subjectId];
      }
      
      // Se selecionou todas, volta para []
      if (newSubjects.length === allSubjects.length) {
        newSubjects = [];
      }
      
      return {
        ...prev,
        [classId]: { selected: true, subjects: newSubjects }
      };
    });
  };

  const isSubjectSelected = (classId, subjectId) => {
    const assignment = selectedAssignments[classId];
    if (!assignment || !assignment.selected) return false;
    
    // Se subjects está vazio, significa todas
    if (!assignment.subjects || assignment.subjects.length === 0) return true;
    
    return assignment.subjects.includes(subjectId);
  };

  const selectAllInCourse = (turmas) => {
    const newAssignments = { ...selectedAssignments };
    turmas.forEach(t => {
      newAssignments[t.id] = { selected: true, subjects: [] };
    });
    setSelectedAssignments(newAssignments);
  };

  const deselectAllInCourse = (turmas) => {
    const newAssignments = { ...selectedAssignments };
    turmas.forEach(t => {
      delete newAssignments[t.id];
    });
    setSelectedAssignments(newAssignments);
  };

  // Selecionar uma disciplina específica em todas as turmas que a possuem
  const selectSubjectInAllClasses = async (subjectName) => {
    setSelectedSubjectFilter(subjectName);
    
    // Carregar disciplinas de todas as turmas
    const newClassSubjects = { ...classSubjects };
    const newAssignments = { ...selectedAssignments };
    let count = 0;
    
    // Filtrar turmas pelo nível de ensino selecionado (corrigido)
    const filteredClasses = selectedEducationLevel === 'all' 
      ? classGroups 
      : classGroups.filter(t => t.education_level === selectedEducationLevel);
    
    for (const turma of filteredClasses) {
      if (!newClassSubjects[turma.id]) {
        try {
          const res = await api.get(`/class-groups/${turma.id}/subjects`);
          newClassSubjects[turma.id] = res.data;
        } catch (error) {
          newClassSubjects[turma.id] = [];
        }
      }
      
      // Verificar se a turma tem essa disciplina
      const subjects = newClassSubjects[turma.id] || [];
      const matchingSubject = subjects.find(s => s.name === subjectName);
      
      if (matchingSubject) {
        // Adicionar/atualizar vinculação com essa disciplina
        const current = newAssignments[turma.id] || { selected: false, subjects: [] };
        const currentSubjects = current.subjects || [];
        
        if (!currentSubjects.includes(matchingSubject.id)) {
          newAssignments[turma.id] = {
            selected: true,
            subjects: currentSubjects.length === 0 ? [matchingSubject.id] : [...currentSubjects, matchingSubject.id]
          };
          count++;
        }
      }
    }
    
    setClassSubjects(newClassSubjects);
    setSelectedAssignments(newAssignments);
    
    const levelLabel = selectedEducationLevel === 'all' ? '' : ` no ${selectedEducationLevel}`;
    toast.success(`"${subjectName}" selecionada em ${count} turma(s)${levelLabel}`);
  };

  // Toggle disciplina na seleção múltipla
  const toggleSubjectMultiple = (subjectName) => {
    setSelectedSubjectsMultiple(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectName)) {
        newSet.delete(subjectName);
      } else {
        newSet.add(subjectName);
      }
      return newSet;
    });
  };

  // Aplicar todas as disciplinas selecionadas de uma vez
  const applyMultipleSubjects = async () => {
    if (selectedSubjectsMultiple.size === 0) {
      toast.error('Selecione pelo menos uma disciplina');
      return;
    }

    setApplyingSubjects(true);
    
    // Carregar disciplinas de todas as turmas
    const newClassSubjects = { ...classSubjects };
    const newAssignments = { ...selectedAssignments };
    let totalCount = 0;
    let turmasEncontradas = 0;
    
    // Filtrar turmas pelo nível de ensino selecionado
    const filteredClasses = selectedEducationLevel === 'all' 
      ? classGroups 
      : classGroups.filter(t => t.education_level === selectedEducationLevel);
    
    turmasEncontradas = filteredClasses.length;
    
    if (turmasEncontradas === 0) {
      toast.error(`Nenhuma turma encontrada para o nível selecionado`);
      setApplyingSubjects(false);
      return;
    }
    
    for (const turma of filteredClasses) {
      if (!newClassSubjects[turma.id]) {
        try {
          const res = await api.get(`/class-groups/${turma.id}/subjects`);
          newClassSubjects[turma.id] = res.data;
        } catch (error) {
          newClassSubjects[turma.id] = [];
        }
      }
      
      const subjects = newClassSubjects[turma.id] || [];
      const current = newAssignments[turma.id] || { selected: false, subjects: [] };
      const currentSubjects = new Set(current.subjects || []);
      let addedToThisClass = false;
      
      // Para cada disciplina selecionada
      for (const subjectName of selectedSubjectsMultiple) {
        const matchingSubject = subjects.find(s => s.name === subjectName);
        if (matchingSubject && !currentSubjects.has(matchingSubject.id)) {
          currentSubjects.add(matchingSubject.id);
          addedToThisClass = true;
        }
      }
      
      if (addedToThisClass || currentSubjects.size > 0) {
        newAssignments[turma.id] = {
          selected: true,
          subjects: Array.from(currentSubjects)
        };
        if (addedToThisClass) totalCount++;
      }
    }
    
    setClassSubjects(newClassSubjects);
    setSelectedAssignments(newAssignments);
    setApplyingSubjects(false);
    
    const levelLabel = selectedEducationLevel === 'all' ? '' : ` no ${selectedEducationLevel}`;
    
    if (totalCount > 0) {
      toast.success(`${selectedSubjectsMultiple.size} disciplina(s) vinculada(s) em ${totalCount} turma(s)${levelLabel}`);
    } else {
      toast.warning(`Disciplinas não encontradas nas ${turmasEncontradas} turmas${levelLabel}. Verifique a matriz curricular.`);
    }
    
    // Limpar seleção múltipla após aplicar
    setSelectedSubjectsMultiple(new Set());
  };

  // Limpar filtro de disciplina
  const clearSubjectFilter = () => {
    setSelectedSubjectFilter(null);
    setSelectedSubjectsMultiple(new Set());
  };

  const handleSave = async () => {
    if (!selectedTeacher) return;
    
    setSaving(true);
    try {
      // Preparar dados para salvar
      const assignments = Object.entries(selectedAssignments)
        .filter(([_, data]) => data.selected)
        .map(([classId, data]) => ({
          class_group_id: classId,
          subject_ids: data.subjects || []
        }));
      
      // Remover vinculações antigas
      await api.delete(`/teacher-assignments/by-teacher/${selectedTeacher.id}?year=${selectedYear}`);
      
      // Criar novas vinculações
      for (const assignment of assignments) {
        await api.post('/teacher-assignments/with-subjects', {
          teacher_id: selectedTeacher.id,
          class_group_id: assignment.class_group_id,
          subject_ids: assignment.subject_ids,
          year: selectedYear
        });
      }
      
      toast.success(`${assignments.length} vinculações salvas com sucesso!`);
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar vinculações');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!confirm('Tem certeza que deseja excluir esta vinculação?')) return;
    
    try {
      await api.delete(`/teacher-assignments/${assignmentId}`);
      toast.success('Vinculação excluída!');
      
      if (selectedTeacher) {
        const res = await api.get(`/teacher-assignments?teacher_id=${selectedTeacher.id}`);
        setTeacherAssignments(res.data);
        
        const assignments = {};
        res.data.forEach(a => {
          assignments[a.class_group_id] = {
            selected: true,
            subjects: a.subject_ids || []
          };
        });
        setSelectedAssignments(assignments);
      }
    } catch (error) {
      toast.error('Erro ao excluir vinculação');
    }
  };

  // Excluir todas as vinculações de um professor
  const handleDeleteAllAssignments = async (teacher) => {
    if (!confirm(`Tem certeza que deseja excluir TODAS as vinculações de ${teacher.full_name || teacher.name}?`)) return;
    
    try {
      const result = await api.delete(`/teacher-assignments/by-teacher/${teacher.id}?year=${selectedYear}`);
      toast.success(`${result.data.count || 0} vinculações excluídas de ${teacher.full_name || teacher.name}`);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir vinculações');
    }
  };

  // Ocultar professor da lista
  const handleHideTeacher = (teacherId) => {
    const newHidden = new Set(hiddenTeachers);
    newHidden.add(teacherId);
    setHiddenTeachers(newHidden);
    localStorage.setItem('hidden_teachers', JSON.stringify([...newHidden]));
    toast.success('Professor ocultado da lista');
  };

  // Restaurar professor à lista
  const handleShowTeacher = (teacherId) => {
    const newHidden = new Set(hiddenTeachers);
    newHidden.delete(teacherId);
    setHiddenTeachers(newHidden);
    localStorage.setItem('hidden_teachers', JSON.stringify([...newHidden]));
    toast.success('Professor restaurado na lista');
  };

  // Restaurar todos os professores ocultos
  const handleShowAllTeachers = () => {
    setHiddenTeachers(new Set());
    localStorage.removeItem('hidden_teachers');
    toast.success('Todos os professores restaurados');
  };

  // Excluir professor oculto permanentemente (remove da lista de professores e exclui vinculações)
  const handleDeleteHiddenTeacher = async (teacher) => {
    const displayName = getDisplayName(teacher);
    
    if (!confirm(`⚠️ ATENÇÃO: Tem certeza que deseja EXCLUIR PERMANENTEMENTE o professor "${displayName}"?\n\nIsso irá:\n- Remover o professor do sistema\n- Excluir todas as vinculações existentes\n\nEsta ação NÃO pode ser desfeita!`)) {
      return;
    }
    
    try {
      // 1. Excluir todas as vinculações do professor
      await api.delete(`/teacher-assignments/by-teacher/${teacher.id}?year=${selectedYear}`);
      
      // 2. Excluir o funcionário
      await api.delete(`/employees/${teacher.id}`);
      
      // 3. Remover da lista de ocultos
      const newHidden = new Set(hiddenTeachers);
      newHidden.delete(teacher.id);
      setHiddenTeachers(newHidden);
      localStorage.setItem('hidden_teachers', JSON.stringify([...newHidden]));
      
      // 4. Recarregar dados
      loadData();
      
      toast.success(`Professor "${displayName}" excluído permanentemente`);
    } catch (error) {
      console.error('Erro ao excluir professor:', error);
      toast.error('Erro ao excluir professor. Verifique se não há dependências.');
    }
  };

  // Exibir nome completo ou composto quando houver duplicidade
  const getDisplayName = (teacher) => {
    // Se tem full_name e é diferente de name, usar full_name
    if (teacher.full_name && teacher.full_name !== teacher.name) {
      return teacher.full_name;
    }
    return teacher.name;
  };

  const filteredTeachers = teachers.filter(t => {
    // Filtrar por busca
    const displayName = getDisplayName(t);
    const matchesSearch = displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtrar ocultos
    const isHidden = hiddenTeachers.has(t.id);
    
    if (showHidden) {
      // Modo "mostrar ocultos": só mostra os ocultos
      return matchesSearch && isHidden;
    } else {
      // Modo normal: não mostra os ocultos
      return matchesSearch && !isHidden;
    }
  });

  const hiddenCount = teachers.filter(t => hiddenTeachers.has(t.id)).length;

  const getSelectedCount = () => {
    return Object.values(selectedAssignments).filter(a => a.selected).length;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="teacher-assignments-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vinculação de Professores</h1>
          <p className="text-muted-foreground mt-1">
            Vincule professores às turmas e disciplinas
          </p>
        </div>
      </div>

      {/* Busca */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Buscar professor por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Botões de controle de visibilidade */}
            <div className="flex gap-2">
              {hiddenCount > 0 && (
                <>
                  <Button
                    variant={showHidden ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowHidden(!showHidden)}
                    className="whitespace-nowrap"
                  >
                    {showHidden ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                    {showHidden ? 'Ver Ativos' : `Ocultos (${hiddenCount})`}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShowAllTeachers}
                    className="whitespace-nowrap text-green-600 hover:bg-green-50"
                    title="Restaurar todos os professores ocultos"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restaurar Todos
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Professores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {showHidden ? `Professores Ocultos (${filteredTeachers.length})` : `Professores (${filteredTeachers.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTeachers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {showHidden ? 'Nenhum professor oculto' : 'Nenhum professor encontrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left font-medium">Nome</th>
                    <th className="p-3 text-left font-medium">Email</th>
                    <th className="p-3 text-left font-medium">Cargo</th>
                    <th className="p-3 text-center font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeachers.map((teacher) => (
                    <tr key={teacher.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 font-medium">{getDisplayName(teacher)}</td>
                      <td className="p-3 text-muted-foreground">{teacher.email}</td>
                      <td className="p-3">
                        <Badge variant="outline">{teacher.role || 'Professor'}</Badge>
                      </td>
                      <td className="p-3 text-center space-x-2">
                        {showHidden ? (
                          /* Modo ocultos: botões para restaurar ou excluir permanentemente */
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleShowTeacher(teacher.id)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Restaurar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteHiddenTeacher(teacher)}
                              title="Excluir professor permanentemente da lista"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </Button>
                          </div>
                        ) : (
                          /* Modo normal: botões de vincular, excluir e ocultar */
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openTeacherDialog(teacher)}
                              data-testid={`vincular-btn-${teacher.id}`}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Vincular
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteAllAssignments(teacher)}
                              title="Excluir todas vinculações"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-orange-600 hover:bg-orange-50"
                              onClick={() => handleHideTeacher(teacher.id)}
                              title="Ocultar professor da lista"
                            >
                              <EyeOff className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Vinculação */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              Habilitações Docentes
            </DialogTitle>
            <DialogDescription>
              Funcionário: <strong>{selectedTeacher?.full_name || selectedTeacher?.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="habilitacoes" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="habilitacoes">Por Turmas</TabsTrigger>
              <TabsTrigger value="disciplina" onClick={loadAllSubjects}>Por Disciplina</TabsTrigger>
              <TabsTrigger value="vinculacoes">Atuais ({teacherAssignments.length})</TabsTrigger>
            </TabsList>

            {/* ABA: Por Disciplina - REDESENHADA */}
            <TabsContent value="disciplina" className="flex-1 overflow-hidden flex flex-col space-y-3 mt-4">
              
              {/* Header com instrução compacta */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Vinculação Rápida por Disciplina</h3>
                    <p className="text-sm text-gray-600">
                      Selecione o nível → Marque as disciplinas → Clique em <span className="font-semibold text-purple-600">Aplicar</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* PASSO 1: Seleção de Nível */}
              <div className="bg-white border rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-medium text-gray-700">Selecione o Nível de Ensino</span>
                  {selectedEducationLevel !== 'all' && (
                    <Badge className="ml-auto bg-purple-100 text-purple-700">
                      {classGroups.filter(t => t.education_level === selectedEducationLevel).length} turmas
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {educationLevels.map(level => {
                    const count = level.value === 'all' 
                      ? classGroups.length 
                      : classGroups.filter(t => t.education_level === level.value).length;
                    const isSelected = selectedEducationLevel === level.value;
                    return (
                      <Button
                        key={level.value}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedEducationLevel(level.value)}
                        className={`h-9 transition-all ${isSelected ? 'bg-purple-600 hover:bg-purple-700 shadow-md' : 'hover:border-purple-300'}`}
                      >
                        {level.label}
                        <span className={`ml-1.5 text-xs ${isSelected ? 'text-purple-200' : 'text-gray-400'}`}>({count})</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* PASSO 2: Seleção de Disciplinas */}
              <div className="flex-1 bg-white border rounded-xl shadow-sm overflow-hidden flex flex-col">
                <div className="p-4 border-b bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                      <span className="font-medium text-gray-700">Marque as Disciplinas</span>
                    </div>
                    {allSubjects.length > 0 && (
                      <span className="text-xs text-gray-500">{allSubjects.length} disciplinas disponíveis</span>
                    )}
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto p-3" style={{maxHeight: '220px'}}>
                  {loadingAllSubjects ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-3" />
                      <p className="text-sm text-gray-500">Carregando disciplinas...</p>
                    </div>
                  ) : allSubjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8">
                      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <BookOpen className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 mb-3">Nenhuma disciplina carregada</p>
                      <Button 
                        variant="outline" 
                        onClick={loadAllSubjects}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Carregar Disciplinas
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {allSubjects.map(subject => {
                        const isSelected = selectedSubjectsMultiple.has(subject.name);
                        return (
                          <div
                            key={subject.id}
                            onClick={() => toggleSubjectMultiple(subject.name)}
                            className={`
                              flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all
                              ${isSelected 
                                ? 'bg-purple-100 border-purple-400 shadow-sm ring-1 ring-purple-300' 
                                : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                              }
                            `}
                          >
                            <Checkbox 
                              checked={isSelected}
                              className={`pointer-events-none ${isSelected ? 'border-purple-500 data-[state=checked]:bg-purple-600' : ''}`}
                            />
                            <span className={`text-sm truncate ${isSelected ? 'font-medium text-purple-800' : 'text-gray-700'}`}>
                              {subject.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* PASSO 3: Barra de Ação Fixa */}
              <div className={`
                rounded-xl p-4 transition-all
                ${selectedSubjectsMultiple.size > 0 
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 shadow-lg' 
                  : 'bg-gray-50 border border-gray-200'
                }
              `}>
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    selectedSubjectsMultiple.size > 0 ? 'bg-green-600 text-white' : 'bg-gray-400 text-white'
                  }`}>3</div>
                  
                  {selectedSubjectsMultiple.size > 0 ? (
                    <>
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {Array.from(selectedSubjectsMultiple).slice(0, 5).map(name => (
                            <Badge key={name} className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer" onClick={() => toggleSubjectMultiple(name)}>
                              {name}
                              <X className="w-3 h-3 ml-1" />
                            </Badge>
                          ))}
                          {selectedSubjectsMultiple.size > 5 && (
                            <Badge className="bg-green-200 text-green-800">
                              +{selectedSubjectsMultiple.size - 5} mais
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-green-700">
                          {selectedSubjectsMultiple.size} disciplina(s) • {selectedEducationLevel === 'all' ? 'Todas as turmas' : selectedEducationLevel}
                          {' '} • {classGroups.filter(t => selectedEducationLevel === 'all' || t.education_level === selectedEducationLevel).length} turmas
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          size="sm" 
                          onClick={clearSubjectFilter}
                          className="text-red-600 hover:bg-red-50 border-red-200"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Limpar
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={applyMultipleSubjects}
                          disabled={applyingSubjects}
                          className="bg-green-600 hover:bg-green-700 shadow-md px-6"
                        >
                          {applyingSubjects ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando...</>
                          ) : (
                            <><Save className="w-4 h-4 mr-2" /> Aplicar nas Turmas</>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Selecione disciplinas acima para vincular ao professor
                    </p>
                  )}
                </div>
              </div>

              {/* Resumo */}
              <div className="flex items-center justify-between text-sm px-1">
                <span className="text-gray-500">
                  <strong className="text-purple-600">{getSelectedCount()}</strong> turma(s) selecionada(s) para vinculação
                </span>
                {getSelectedCount() > 0 && (
                  <span className="text-green-600 font-medium">✓ Pronto para salvar</span>
                )}
              </div>
            </TabsContent>

            <TabsContent value="habilitacoes" className="flex-1 overflow-hidden flex flex-col space-y-4 mt-4">
              {/* Filtros */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="onlyActive"
                      checked={onlyActive}
                      onCheckedChange={setOnlyActive}
                    />
                    <Label htmlFor="onlyActive" className="text-sm">Somente Ativos</Label>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Label className="text-sm">Ano Letivo:</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setSelectedYear(y => y - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="w-16 text-center font-medium">{selectedYear}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setSelectedYear(y => y + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Aviso */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <strong>Importante:</strong> Selecione as turmas e clique para expandir e escolher as disciplinas específicas.
                  As disciplinas selecionadas serão as únicas disponíveis para este professor no módulo de horários.
                </div>
              </div>

              {/* Lista de Turmas */}
              <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {Object.entries(availableClasses).map(([curso, turmas]) => (
                    <div key={curso} className="border rounded-lg">
                      {/* Cabeçalho do Curso */}
                      <div
                        className="p-2 bg-muted/30 flex items-center justify-between cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleCourse(curso)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{expandedCourses.has(curso) ? '▼' : '▶'}</span>
                          <span className="font-medium">{curso}</span>
                          <Badge variant="secondary" className="ml-2">{turmas.length} turmas</Badge>
                        </div>
                        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => selectAllInCourse(turmas)}
                          >
                            Todas
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs"
                            onClick={() => deselectAllInCourse(turmas)}
                          >
                            Nenhuma
                          </Button>
                        </div>
                      </div>
                      
                      {/* Lista de Turmas do Curso */}
                      {expandedCourses.has(curso) && (
                        <div className="p-2 space-y-1">
                          {turmas
                            .filter(t => !onlyActive || t.status !== 'inactive')
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(turma => (
                              <div key={turma.id} className="border rounded">
                                {/* Linha da Turma */}
                                <div
                                  className={`flex items-center gap-2 p-2 hover:bg-muted/30 ${
                                    selectedAssignments[turma.id]?.selected ? 'bg-primary/10' : ''
                                  }`}
                                >
                                  <Checkbox
                                    checked={!!selectedAssignments[turma.id]?.selected}
                                    onCheckedChange={() => toggleClass(turma.id)}
                                  />
                                  <span 
                                    className="text-sm flex-1 cursor-pointer"
                                    onClick={() => toggleClass(turma.id)}
                                  >
                                    {turma.name}
                                  </span>
                                  {turma.shift && (
                                    <Badge variant="outline" className="text-xs">
                                      {turma.shift}
                                    </Badge>
                                  )}
                                  {selectedAssignments[turma.id]?.selected && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2"
                                      onClick={() => toggleClassExpansion(turma.id)}
                                    >
                                      <BookOpen className="w-4 h-4 mr-1" />
                                      Disciplinas
                                      {expandedClasses.has(turma.id) ? (
                                        <ChevronUp className="w-4 h-4 ml-1" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4 ml-1" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                                
                                {/* Disciplinas da Turma */}
                                {expandedClasses.has(turma.id) && selectedAssignments[turma.id]?.selected && (
                                  <div className="border-t bg-muted/20 p-2">
                                    {loadingSubjects[turma.id] ? (
                                      <div className="flex items-center justify-center py-2">
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        Carregando disciplinas...
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {(classSubjects[turma.id] || []).map(subject => (
                                          <div
                                            key={subject.id}
                                            className={`flex items-center gap-2 p-1.5 rounded cursor-pointer hover:bg-muted/50 ${
                                              isSubjectSelected(turma.id, subject.id) ? 'bg-green-100' : ''
                                            }`}
                                            onClick={() => toggleSubject(turma.id, subject.id)}
                                          >
                                            <Checkbox
                                              checked={isSubjectSelected(turma.id, subject.id)}
                                              onCheckedChange={() => toggleSubject(turma.id, subject.id)}
                                            />
                                            <span className="text-xs">{subject.name}</span>
                                          </div>
                                        ))}
                                        {(classSubjects[turma.id] || []).length === 0 && (
                                          <div className="col-span-3 text-center text-sm text-muted-foreground py-2">
                                            Nenhuma disciplina encontrada na matriz curricular
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      {selectedAssignments[turma.id]?.subjects?.length === 0 
                                        ? '✓ Todas as disciplinas selecionadas'
                                        : `${selectedAssignments[turma.id]?.subjects?.length || 0} disciplina(s) selecionada(s)`
                                      }
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Resumo */}
              <div className="text-sm text-muted-foreground">
                {getSelectedCount()} turma(s) selecionada(s)
              </div>
            </TabsContent>

            {/* Aba de Vinculações Atuais */}
            <TabsContent value="vinculacoes" className="flex-1 overflow-auto mt-4">
              <div className="space-y-2">
                {teacherAssignments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhuma vinculação cadastrada
                  </div>
                ) : (
                  <div className="border rounded-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="p-2 text-left text-sm">Turma</th>
                          <th className="p-2 text-left text-sm">Disciplinas</th>
                          <th className="p-2 text-center text-sm">Ano</th>
                          <th className="p-2 text-center text-sm">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherAssignments.map(assignment => {
                          const turma = classGroups.find(c => c.id === assignment.class_group_id);
                          const numDisciplinas = assignment.subject_ids?.length || 0;
                          return (
                            <tr key={assignment.id} className="border-b hover:bg-muted/30">
                              <td className="p-2 text-sm">{turma?.name || 'Turma não encontrada'}</td>
                              <td className="p-2 text-sm">
                                {numDisciplinas === 0 ? (
                                  <Badge variant="outline" className="bg-green-50">Todas</Badge>
                                ) : (
                                  <Badge variant="secondary">{numDisciplinas} disciplina(s)</Badge>
                                )}
                              </td>
                              <td className="p-2 text-center text-sm">{assignment.year}</td>
                              <td className="p-2 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteAssignment(assignment.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Sair
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
